import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import BackBar from '../components/BackBar';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WasteRoomBuilder = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showGuide, setShowGuide] = useState(true);

  useEffect(() => { loadRooms(); }, []);

  const loadRooms = async () => {
    try {
      const res = await axios.get(`${API}/waste-rooms`, { headers: { Authorization: `Bearer ${token}` } });
      setRooms(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!newRoomName.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/waste-rooms`, { name: newRoomName.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      navigate(`/waste-room/${res.data.id}`);
    } catch (err) { alert('Failed to create room'); }
    finally { setCreating(false); }
  };

  const handleDelete = async (e, roomId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this waste room project?')) return;
    try {
      await axios.delete(`${API}/waste-rooms/${roomId}`, { headers: { Authorization: `Bearer ${token}` } });
      setRooms(prev => prev.filter(r => r.id !== roomId));
    } catch (err) { alert('Failed to delete'); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <BackBar to="/dashboard" label="Dashboard" />
      <SEO title="Waste Room Builder" description="Visual waste room planning tool" noIndex={true} />
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <img src="/plangen-consulting-logo.png" alt="PlanGen" className="h-10 w-auto" />
            </div>
            <div className="flex items-center space-x-4">
              <button onClick={() => navigate('/dashboard')} className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1" data-testid="back-dashboard">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4" /></svg>
                Dashboard
              </button>
              <span className="text-sm text-gray-700">{user?.full_name}</span>
              <button onClick={() => { logout(); navigate('/login'); }} className="text-sm text-gray-600 hover:text-gray-900">Logout</button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="wrb-title">Waste Room Builder</h1>
            <p className="mt-1 text-sm text-gray-600">Design compliant waste storage rooms with real equipment at true scale</p>
          </div>
          <button onClick={() => setShowCreateModal(true)} className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium" data-testid="new-waste-room-btn">
            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            New Waste Room
          </button>
        </div>

        <p className="text-xs text-gray-500 mb-4 italic">Disclaimer: Not certified engineering output — final sign-off required by qualified engineer.</p>

        {/* How to Use */}
        <div className="bg-white rounded-xl border border-gray-200 mb-6" data-testid="how-to-use-card">
          <button onClick={() => setShowGuide(!showGuide)} className="w-full flex items-center justify-between px-5 py-3 text-left" data-testid="how-to-use-toggle">
            <span className="text-sm font-semibold text-gray-700">How to Use</span>
            <svg className={`w-4 h-4 text-gray-400 transition-transform ${showGuide ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </button>
          {showGuide && (
            <div className="px-5 pb-4 border-t border-gray-100 pt-3">
              <ol className="space-y-1.5 text-sm text-gray-600">
                <li><span className="font-semibold text-gray-800">1.</span> Create a room and upload your floor plan image (PNG/JPG)</li>
                <li><span className="font-semibold text-gray-800">2.</span> Set the scale — pick a preset <strong>or</strong> use <strong>Calibrate</strong> (click two points with a known distance for accuracy)</li>
                <li><span className="font-semibold text-gray-800">3.</span> Enter room dimensions (width and depth in mm)</li>
                <li><span className="font-semibold text-gray-800">4.</span> Drag equipment from the sidebar onto the canvas — rotate, duplicate, and position as needed</li>
                <li><span className="font-semibold text-gray-800">5.</span> Check the <strong>Analysis</strong> panel for clearance warnings and compliance status</li>
                <li><span className="font-semibold text-gray-800">6.</span> Export to <strong>PDF</strong> with room diagram and bill of materials</li>
              </ol>
              <p className="text-xs text-gray-400 mt-3"><strong>Calibrate vs Scale:</strong> Scale presets (1:50, 1:100, etc.) give approximate measurements. For best accuracy, use Calibrate — click two points on your plan where you know the real-world distance, then enter that distance in mm.</p>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12"><p className="text-gray-500">Loading...</p></div>
        ) : rooms.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-300">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" /></svg>
            <h3 className="mt-3 text-sm font-medium text-gray-900">No waste rooms yet</h3>
            <p className="mt-1 text-sm text-gray-500">Create your first waste room layout.</p>
            <button onClick={() => setShowCreateModal(true)} className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700">
              Create Your First Room
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {rooms.map(room => (
              <div key={room.id} onClick={() => navigate(`/waste-room/${room.id}`)} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg transition-shadow cursor-pointer group" data-testid={`room-card-${room.id}`}>
                <div className="flex items-start justify-between">
                  <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">{room.name}</h3>
                  <button onClick={(e) => handleDelete(e, room.id)} className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-room-${room.id}`}>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
                {room.description && <p className="text-sm text-gray-500 mt-1">{room.description}</p>}
                <div className="mt-3 flex items-center gap-3 text-xs text-gray-500">
                  {room.room_width_mm && room.room_depth_mm ? (
                    <span>{(room.room_width_mm/1000).toFixed(1)}m x {(room.room_depth_mm/1000).toFixed(1)}m</span>
                  ) : (
                    <span className="text-amber-600">Not calibrated</span>
                  )}
                  <span>v{room.version || 1}</span>
                </div>
                <div className="mt-2 text-xs text-gray-400">Updated {new Date(room.updated_at).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-gray-900/60" onClick={() => setShowCreateModal(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6 z-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">New Waste Room</h2>
            <input type="text" placeholder="Room name (e.g. Building A - Level B1)" value={newRoomName} onChange={e => setNewRoomName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" data-testid="new-room-name-input" autoFocus onKeyDown={e => e.key === 'Enter' && handleCreate()} />
            <div className="mt-4 flex gap-3 justify-end">
              <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
              <button onClick={handleCreate} disabled={creating || !newRoomName.trim()} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50" data-testid="create-room-submit">
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WasteRoomBuilder;
